import React, { useEffect, useState, useMemo } from "react";
import TitlePages from "../../layout/TitlePages";
import Breadcrumbs from "../../layout/Breadcrumbs";
import { dataITEConecta } from "../../data/dataITEConecta";
import "./ITEConecta.css";

// Icono vectorial moderno de claqueta / rollo de película con botón Play
const FilmVideoIcon = () => (
  <svg
    className="ite-conecta-film-svg"
    viewBox="0 0 54 44"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    {/* Marco exterior de película */}
    <rect
      x="3"
      y="3"
      width="48"
      height="38"
      rx="7"
      fill="#2A2A38"
      stroke="#454558"
      strokeWidth="1.5"
    />
    {/* Perforaciones superiores */}
    <rect x="7" y="6.5" width="4.5" height="4.5" rx="1" fill="#FFFFFF" fillOpacity="0.9" />
    <rect x="15.5" y="6.5" width="4.5" height="4.5" rx="1" fill="#FFFFFF" fillOpacity="0.9" />
    <rect x="24" y="6.5" width="4.5" height="4.5" rx="1" fill="#FFFFFF" fillOpacity="0.9" />
    <rect x="32.5" y="6.5" width="4.5" height="4.5" rx="1" fill="#FFFFFF" fillOpacity="0.9" />
    <rect x="41" y="6.5" width="4.5" height="4.5" rx="1" fill="#FFFFFF" fillOpacity="0.9" />

    {/* Perforaciones inferiores */}
    <rect x="7" y="33" width="4.5" height="4.5" rx="1" fill="#FFFFFF" fillOpacity="0.9" />
    <rect x="15.5" y="33" width="4.5" height="4.5" rx="1" fill="#FFFFFF" fillOpacity="0.9" />
    <rect x="24" y="33" width="4.5" height="4.5" rx="1" fill="#FFFFFF" fillOpacity="0.9" />
    <rect x="32.5" y="33" width="4.5" height="4.5" rx="1" fill="#FFFFFF" fillOpacity="0.9" />
    <rect x="41" y="33" width="4.5" height="4.5" rx="1" fill="#FFFFFF" fillOpacity="0.9" />

    {/* Pantalla central */}
    <rect x="11" y="13.5" width="32" height="17" rx="3" fill="#14141E" />

    {/* Botón circular rojo de reproducción */}
    <circle cx="27" cy="22" r="7.5" fill="url(#playRedGrad)" />

    {/* Flecha blanca Play */}
    <polygon points="25.5,18.5 25.5,25.5 30.5,22" fill="#FFFFFF" />

    <defs>
      <linearGradient id="playRedGrad" x1="20" y1="15" x2="34" y2="29" gradientUnits="userSpaceOnUse">
        <stop stopColor="#f44336" />
        <stop offset="1" stopColor="#b71c1c" />
      </linearGradient>
    </defs>
  </svg>
);

// Icono sutil de calendario para fechas
const CalendarIcon = () => (
  <svg
    className="ite-fecha-icon"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

const ITEConecta = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  useEffect(() => {
    document.title = `ITE Conecta`;
  }, []);

  // Lista ordenada de mayor a menor por ID
  const allVideos = useMemo(() => {
    return [...dataITEConecta].sort((a, b) => b.id - a.id);
  }, []);

  // Filtrado reactivo en tiempo real por término de búsqueda
  const filteredVideos = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return allVideos;
    return allVideos.filter(
      (v) =>
        String(v.id).includes(term) ||
        (v.title && v.title.toLowerCase().includes(term)) ||
        (v.fecha && v.fecha.toLowerCase().includes(term))
    );
  }, [allVideos, searchTerm]);

  // Total de páginas
  const totalItems = filteredVideos.length;
  const isAll = pageSize === -1;
  const totalPages = isAll ? 1 : Math.ceil(totalItems / pageSize) || 1;

  // Ajustar página activa si el filtro reduce el total de páginas
  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(1);
    }
  }, [totalPages, currentPage]);

  // Elementos visibles en la página actual
  const paginatedVideos = useMemo(() => {
    if (isAll) return filteredVideos;
    const startIndex = (currentPage - 1) * pageSize;
    return filteredVideos.slice(startIndex, startIndex + pageSize);
  }, [filteredVideos, currentPage, pageSize, isAll]);

  const startIndex = isAll ? 1 : totalItems === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const endIndex = isAll ? totalItems : Math.min(currentPage * pageSize, totalItems);

  return (
    <>
      <Breadcrumbs
        path={[{ label: "Comunicación Social" }, { label: "ITE Conecta" }]}
      />
      <TitlePages title="Comunicación Social" subTitle="ITE Conecta" />

      <div className="ite-conecta-wrapper">
        {/* Barra superior de herramientas: contador y buscador */}
        <div className="ite-conecta-toolbar">
          <div className="ite-conecta-count-badge">
            <svg
              className="ite-conecta-count-icon"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <polygon points="23 7 16 12 23 17 23 7" />
              <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
            </svg>
            <span>{totalItems} {totalItems === 1 ? "Programa" : "Programas emitidos"}</span>
          </div>

          <div className="ite-conecta-search-box">
            <svg
              className="ite-conecta-search-icon"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              type="text"
              className="ite-conecta-search-input"
              placeholder="Buscar por programa, tema o fecha..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              aria-label="Buscar programas de ITE Conecta"
            />
            {searchTerm && (
              <button
                type="button"
                className="ite-conecta-clear-btn"
                onClick={() => setSearchTerm("")}
                title="Limpiar búsqueda"
                aria-label="Limpiar búsqueda"
              >
                ✕
              </button>
            )}
          </div>
        </div>

        {/* Tarjeta formal que contiene la tabla estilizada */}
        <div className="ite-conecta-table-card">
          <div className="ite-conecta-table-responsive">
            <table className="ite-conecta-table">
              <thead>
                <tr>
                  <th className="th-center" style={{ width: "16%" }}>
                    No. de Programa
                  </th>
                  <th className="th-center" style={{ width: "16%" }}>
                    Fecha
                  </th>
                  <th style={{ width: "53%" }}>Nombre</th>
                  <th className="th-center" style={{ width: "15%" }}>
                    Video
                  </th>
                </tr>
              </thead>
              <tbody>
                {paginatedVideos.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="ite-conecta-empty-state">
                      No se encontraron programas que coincidan con &ldquo;{searchTerm}&rdquo;.
                    </td>
                  </tr>
                ) : (
                  paginatedVideos.map(({ id, title, fecha }) => (
                    <tr key={id}>
                      {/* Número de programa con píldora distintiva */}
                      <td className="td-center">
                        <span className="ite-programa-pill">{id}</span>
                      </td>

                      {/* Fecha con icono */}
                      <td className="td-center">
                        <span className="ite-fecha-cell">
                          <CalendarIcon />
                          <span>{fecha}</span>
                        </span>
                      </td>

                      {/* Nombre descriptivo del programa */}
                      <td>
                        <span className="ite-nombre-cell">{title}</span>
                      </td>

                      {/* Botón interactivo con icono moderno de película */}
                      <td className="td-center">
                        <a
                          href={`https://itetlax.org.mx/assets/videos/ite-conecta/${id}.mp4`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="ite-conecta-video-btn"
                          title={`Ver programa ${id}: ${title}`}
                          aria-label={`Reproducir video del programa ${id}`}
                        >
                          <FilmVideoIcon />
                        </a>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Barra de paginación formal inferior */}
          {totalItems > 0 && (
            <div className="ite-conecta-pagination-bar">
              <div className="ite-conecta-pagination-info">
                Mostrando {startIndex}–{endIndex} de {totalItems} programas
              </div>

              <div className="ite-conecta-pagination-controls">
                <button
                  type="button"
                  className="ite-conecta-page-btn"
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1 || isAll}
                  title="Primera página"
                  aria-label="Primera página"
                >
                  «
                </button>
                <button
                  type="button"
                  className="ite-conecta-page-btn"
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1 || isAll}
                  title="Página anterior"
                  aria-label="Página anterior"
                >
                  ‹
                </button>

                {!isAll && (
                  <span className="ite-conecta-page-btn is-active">
                    {currentPage} / {totalPages}
                  </span>
                )}

                <button
                  type="button"
                  className="ite-conecta-page-btn"
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages || isAll}
                  title="Página siguiente"
                  aria-label="Página siguiente"
                >
                  ›
                </button>
                <button
                  type="button"
                  className="ite-conecta-page-btn"
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages || isAll}
                  title="Última página"
                  aria-label="Última página"
                >
                  »
                </button>

                {/* Selector de filas por página */}
                <select
                  className="ite-conecta-rows-select"
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  aria-label="Filas por página"
                >
                  <option value={10}>10 filas</option>
                  <option value={20}>20 filas</option>
                  <option value={50}>50 filas</option>
                  <option value={-1}>Todas</option>
                </select>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ITEConecta;
